import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Check, CreditCard, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export const ROLE_PLANS = {
  creator: {
    label: "Creator",
    price: 249,
    perks: [
      "Sell music, merch and tickets on your storefront",
      "Groove Vibe, Forge, Lens and Enhance studio tools",
      "Take paid bookings and withdraw earnings",
    ],
  },
  venue: {
    label: "Venue",
    price: 849,
    perks: [
      "Publish events and sell tickets at the door",
      "Groove Design, Space, Planner and Insights",
      "Book resident creators and manage your team",
    ],
  },
} as const;

export type UpgradeRole = keyof typeof ROLE_PLANS;

interface Props {
  role: UpgradeRole | null;
  onOpenChange: (open: boolean) => void;
}

const RoleUpgradeDialog = ({ role, onOpenChange }: Props) => {
  const [loading, setLoading] = useState(false);
  const plan = role ? ROLE_PLANS[role] : null;

  const pay = async () => {
    if (!role) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("role-upgrade", {
        body: {
          action: "initialize",
          target_role: role,
          callback_url: `${window.location.origin}/payment/return?kind=upgrade`,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      if (!data?.authorization_url) throw new Error("Payment could not be started");
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not start payment");
      setLoading(false);
    }
  };

  // Access unlocks the moment the payment clears; the plan renews monthly from today.
  const today = new Date();
  const renewal = new Date(today);
  renewal.setMonth(renewal.getMonth() + 1);
  const fmt = (d: Date) =>
    d.toLocaleDateString("en-ZA", { day: "numeric", month: "long", year: "numeric" });

  return (
    <Dialog open={!!role} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-white sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white">{plan?.label} Pro — R{plan?.price.toLocaleString()}/month</DialogTitle>
          <DialogDescription className="text-gray-400">
            This page is part of the {plan?.label} Pro add-on. Upgrade in one tap and it unlocks immediately.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-5">
          <div className="flex items-baseline gap-1">
            <span className="text-3xl font-bold text-white">R{plan?.price.toLocaleString()}</span>
            <span className="text-sm text-gray-400">/ month</span>
          </div>
          <dl className="mt-3 space-y-1 text-xs text-gray-400">
            <div className="flex justify-between"><dt>Plan</dt><dd className="text-gray-200">{plan?.label} Pro (monthly)</dd></div>
            <div className="flex justify-between"><dt>Effective from</dt><dd className="text-gray-200">{fmt(today)}</dd></div>
            <div className="flex justify-between"><dt>Renews on</dt><dd className="text-gray-200">{fmt(renewal)}</dd></div>
          </dl>
          <ul className="mt-4 space-y-2">
            {plan?.perks.map((p) => (
              <li key={p} className="flex gap-2 text-sm text-gray-300">
                <Check className="h-4 w-4 shrink-0 text-emerald-400 mt-0.5" /> {p}
              </li>
            ))}
          </ul>
        </div>

        {role === "venue" && (
          <p className="flex gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200">
            <ShieldCheck className="h-4 w-4 shrink-0" />
            After payment you must verify your company details and upload certified documents before your venue goes live.
          </p>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>Cancel</Button>
          <Button onClick={pay} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CreditCard className="h-4 w-4 mr-2" />}
            Upgrade now — R{plan?.price.toLocaleString()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RoleUpgradeDialog;
